import { useQuery } from "@tanstack/react-query";
import {
  CALCULATION_CYCLE_POLL_INTERVAL_MS,
  CALCULATION_CYCLE_STALE_TIME_MS,
} from "@/features/tuition/constants";
import type { CalculationCycle } from "@/features/tuition/types";
import {
  fetchCurrentCalculationCycle,
  getCurrentCalculationCycleQueryKey,
} from "@/features/tuition/utils/fetch-calculation-cycles";
import { getCalculationCyclePollInterval } from "@/features/tuition/utils/is-tuition-calculation-running";
import { useAccessToken, useTenantData } from "@/hooks";

interface UseCalculationCycleCurrentProps {
  enabled?: boolean;
  poll?: boolean;
  /** Keep polling after Post until the cycle reaches Synced or SyncFailed. */
  pollWhilePendingPost?: boolean;
}

export const useCalculationCycleCurrent = ({
  enabled = true,
  poll = true,
  pollWhilePendingPost = false,
}: UseCalculationCycleCurrentProps = {}) => {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();

  const {
    data,
    isError,
    isLoading,
    isPending,
    isFetching,
    isSuccess,
    refetch,
  } = useQuery({
    queryKey: getCurrentCalculationCycleQueryKey(),
    queryFn: async () => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      return fetchCurrentCalculationCycle(accessToken, tenantId);
    },
    enabled: enabled !== false && !!accessToken,
    staleTime: CALCULATION_CYCLE_STALE_TIME_MS,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchInterval: poll
      ? (query) => {
          const cycle = query.state.data as CalculationCycle | null | undefined;
          const statusInterval = getCalculationCyclePollInterval(cycle);
          if (statusInterval) return statusInterval;
          if (!pollWhilePendingPost) return false;
          return CALCULATION_CYCLE_POLL_INTERVAL_MS;
        }
      : false,
  });

  return {
    data,
    isError,
    isLoading,
    isPending,
    isFetching,
    isSuccess,
    refetch,
  };
};
